import { Container, Row, Col, Card, Badge, Alert } from 'react-bootstrap';
import { FaWhatsapp, FaArrowDown, FaInfoCircle, FaExclamationTriangle } from 'react-icons/fa';

const STEPS = [
  {
    n: 1,
    title: 'Scrape a WhatsApp group',
    channel: 'web',
    body: 'Connect a number by QR scan, then pull group members in as Prospects. Scan / WhatsApp Web is used only for this — scraping contacts out of groups.',
  },
  {
    n: 2,
    title: 'Run a campaign to those prospects',
    channel: 'api',
    body: 'Pick an approved Meta template, map its blanks (field or custom text), add the prospect group as recipients, and start the campaign.',
  },
  {
    n: 3,
    title: 'Prospect replies',
    channel: null,
    body: 'A reply arrives by webhook (Business API) and is stored on the prospect. The prospect status becomes REPLIED.',
  },
  {
    n: 4,
    title: 'Convert prospect → Lead',
    channel: null,
    body: 'Creates a lead + contact + one WhatsApp conversation, and copies the prospect’s message history into it. The person now appears in Conversations.',
  },
  {
    n: 5,
    title: 'Converse / personalise',
    channel: null,
    body: 'From Leads or Conversations, reply freely (within the open 24-hour window) or send personalised templates.',
  },
];

const RULES = [
  {
    variant: 'warning',
    text: 'Template blanks need real data. A template that uses a website value fails for scraped prospects (they have none). Use the contact name or custom text instead.',
  },
  {
    variant: 'info',
    text: 'Prospect replies are not in Conversations until converted. Un-converted prospects live under Prospects → their group. Convert them to a lead to see the thread in the inbox.',
  },
  {
    variant: 'info',
    text: 'One phone number = one lead = one conversation. The system de-duplicates by phone, and deleting a lead hides its conversations.',
  },
];

function ChannelPill({ channel }) {
  if (channel === 'web') return <Badge bg="success" className="ms-2"><FaWhatsapp className="me-1" />Scan / WhatsApp Web</Badge>;
  if (channel === 'api') return <Badge bg="primary" className="ms-2"><FaWhatsapp className="me-1" />WhatsApp Business API</Badge>;
  return null;
}

function HowItWorks() {
  return (
    <Container fluid className="py-3" style={{ maxWidth: 980 }}>
      <h3 className="mb-1">How It Works — WhatsApp Outreach</h3>
      <p className="text-muted">
        Admin reference: the pipeline from finding a contact to a live lead, plus the rules that trip people up.
      </p>

      <h5 className="mt-4 mb-3">The flow</h5>
      {STEPS.map((s, i) => (
        <div key={s.n}>
          <Card>
            <Card.Body className="py-3">
              <div className="d-flex align-items-start">
                <span
                  className="d-inline-flex align-items-center justify-content-center bg-primary text-white rounded-circle me-3 flex-shrink-0"
                  style={{ width: 28, height: 28, fontSize: '.85rem' }}
                >
                  {s.n}
                </span>
                <div>
                  <div className="fw-bold">
                    {s.title}
                    <ChannelPill channel={s.channel} />
                  </div>
                  <div className="text-muted small mt-1">{s.body}</div>
                </div>
              </div>
            </Card.Body>
          </Card>
          {i < STEPS.length - 1 && (
            <div className="text-center text-muted my-2"><FaArrowDown /></div>
          )}
        </div>
      ))}

      <h5 className="mt-4 mb-3">Two WhatsApp channels — don&apos;t mix them up</h5>
      <Row className="g-3">
        <Col md={6}>
          <Card className="h-100">
            <Card.Body>
              <Badge bg="success"><FaWhatsapp className="me-1" />Scan / WhatsApp Web</Badge>
              <ul className="small mt-2 mb-0">
                <li>QR-scan a number</li>
                <li>Used for scraping groups only</li>
                <li>Replies read via polling</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100">
            <Card.Body>
              <Badge bg="primary"><FaWhatsapp className="me-1" />WhatsApp Business API</Badge>
              <ul className="small mt-2 mb-0">
                <li>Meta token + approved templates</li>
                <li>Campaigns + lead messaging</li>
                <li>Replies arrive via webhook (push)</li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <h5 className="mt-4 mb-3">Rules that bite — remember these</h5>
      {RULES.map((r, i) => (
        <Alert key={i} variant={r.variant} className="small d-flex">
          <span className="me-2">
            {r.variant === 'info' ? <FaInfoCircle /> : <FaExclamationTriangle />}
          </span>
          <span>{r.text}</span>
        </Alert>
      ))}
    </Container>
  );
}

export default HowItWorks;
